import * as React from "react";
import {
    Box,
    Divider,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    useMediaQuery,
    Paper,
} from "@mui/material";
import type { Theme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FollowRow from "./FollowRow";
import type { IFollowUser, IFollowersData } from "../types/socialEdges";
import type { Dispatch, SetStateAction } from "react";

interface FollowTabProps {
    data: IFollowersData;
    isLoading?: boolean;
    isProfileOwner?: boolean;
    tabType: "Followers" | "Followings";
    update?: () => Promise<void>;
    setFunction?: Dispatch<SetStateAction<IFollowersData | null>>;
    emptyCopy?: {
        pending?: string;
        approved?: string;
    };
}

const FollowTab: React.FC<FollowTabProps> = ({
    data,
    isLoading = false,
    update,
    setFunction,
    emptyCopy,
    isProfileOwner = false,
    tabType,
}) => {
    const [tab, setTab] = React.useState<0 | 1>(isProfileOwner && tabType !== "Followings" ? 0 : 1);
    const [query, setQuery] = React.useState("");
    const isSmall = useMediaQuery<Theme>((t) => t.breakpoints.down("sm"));
    const [debouncedQuery, setDebouncedQuery] = React.useState(query);

    React.useEffect(() => {
        const id = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 200);
        return () => clearTimeout(id);
    }, [query]);

    const counts = {
        pending: data?.pending?.length ?? 0,
        approved: data?.approved?.length ?? 0,
    };

    const filtered = React.useMemo(() => {
        const currentList = isProfileOwner ? (tab === 0 ? data?.pending ?? [] : data?.approved ?? []) : data?.approved ?? [];

        if (!debouncedQuery) return sortByUsername(currentList);
        return sortByUsername(currentList.filter((f) => f.username.toLowerCase().includes(debouncedQuery)));
    }, [debouncedQuery, data, tab, isProfileOwner]);

    const loadingSkeleton = (
        <List sx={{ px: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
                <ListItem key={`sk-${i}`} disableGutters sx={{ py: 1.25 }}>
                    <ListItemAvatar>
                        <Skeleton variant="circular" width={40} height={40} />
                    </ListItemAvatar>
                    <ListItemText primary={<Skeleton width="40%" />} secondary={<Skeleton width="22%" />} />
                </ListItem>
            ))}
        </List>
    );

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                overflow: "hidden",
            }}
        >
            <Stack direction="column" spacing={0}>
                {/* Header */}
                <Box
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 3 },
                        pb: 1,
                    }}
                >
                    <Stack
                        direction={isSmall ? "column" : "row"}
                        spacing={2}
                        alignItems={isSmall ? "stretch" : "center"}
                        justifyContent="space-between"
                    >
                        <Typography variant="h6" fontWeight={700}>
                            {tabType}
                        </Typography>
                        <TextField
                            size="small"
                            placeholder={"Search " + tabType}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            inputProps={{ "aria-label": "Search followers" }}
                            sx={{ width: isSmall ? "100%" : 320 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </Box>
                <Divider></Divider>
                {/* Tabs */}
                {isProfileOwner && tabType !== "Followings" && (
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        aria-label="Followers tabs"
                        variant="fullWidth"
                        sx={{
                            px: { xs: 1, sm: 2 },
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: "flex" }}>
                                    <Typography>Pending</Typography>
                                    <Divider orientation="vertical" sx={{ mx: 1 }} />
                                    <Typography>{counts.pending}</Typography>
                                </Box>
                            }
                            id="tab-pending"
                            aria-controls="panel-pending"
                        />
                        <Tab
                            label={
                                <Box sx={{ display: "flex" }}>
                                    <Typography>Approved</Typography>
                                    <Divider orientation="vertical" sx={{ mx: 1 }} />
                                    <Typography>{counts.approved}</Typography>
                                </Box>
                            }
                            id="tab-approved"
                            aria-controls="panel-approved"
                        />
                    </Tabs>
                )}

                <Box
                    role="tabpanel"
                    id={tab === 0 ? "panel-pending" : "panel-approved"}
                    aria-labelledby={tab === 0 ? "tab-pending" : "tab-approved"}
                    sx={{ p: { xs: 1, sm: 2 } }}
                >
                    {isLoading ? (
                        loadingSkeleton
                    ) : filtered.length === 0 ? (
                        <EmptyState
                            kind={tab === 0 ? "pending" : "approved"}
                            query={debouncedQuery}
                            message={
                                tab === 0
                                    ? emptyCopy?.pending ?? "No pending " + tabType + " requests."
                                    : emptyCopy?.approved ?? "User doesn't have any " + tabType + " yet."
                            }
                        />
                    ) : (
                        <List sx={{ px: { xs: 0, sm: 1 } }}>
                            {filtered.map((f, idx) => (
                                <React.Fragment key={`${f.username}-${idx}`}>
                                    <FollowRow
                                        setFunction={setFunction}
                                        isProfileOwner={isProfileOwner}
                                        follower={f}
                                        update={update}
                                        tabType={tabType}
                                        mode={tab === 0 ? "pending" : "approved"}
                                    />
                                    {idx < filtered.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>
            </Stack>
        </Paper>
    );
};

const EmptyState = ({ kind, query, message }: { kind: "pending" | "approved"; query?: string; message: string }) => {
    return (
        <Box
            sx={{
                py: 8,
                px: 2,
                textAlign: "center",
                opacity: 0.9,
            }}
        >
            <Typography variant="body1" color="text.secondary">
                {query ? `No followers matching “${query}”.` : message}
            </Typography>
        </Box>
    );
};

const sortByUsername = (list: IFollowUser[]) => {
    return [...list].sort((a, b) => a.username.localeCompare(b.username, undefined, { sensitivity: "base" }));
};

export default FollowTab;
